import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Eye, Pencil, Plus, Power, Printer, RotateCcw } from "lucide-react";
import { usePageActions } from "@/shared/hooks/usePageActions";
import { useToast } from "@/shared/providers/ToastProvider";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { StatusBadge } from "../components/StatusBadge";
import { inputClass, selectClass } from "../components/FormField";
import {
  fetchAdminCompanies,
  fetchAdminInsurers,
  setAdminCompanyStatus,
  type AdminInsurer,
  type InsurerCompany,
} from "../api/insurerApi";
import { ExportMenu } from "../components/ExportMenu";

/** Code affiché d'une entreprise (son identifiant). */
function companyCode(id: number): string {
  return `ENT-${String(id).padStart(4, "0")}`;
}

const iconLink = "inline-flex h-7 w-7 items-center justify-center rounded border";
const toolButton =
  "inline-flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm text-muted-foreground hover:bg-accent/20 hover:border-accent";

/**
 * Admin : entreprises du catalogue, gérées comme les prestataires. Une entreprise existe avec ses agents
 * sans assureur ; elle est ensuite rattachée à un assureur (un seul à la fois).
 */
export function CompaniesPage() {
  const { exportTable, printPage } = usePageActions();
  const { showToast } = useToast();
  const [companies, setCompanies] = useState<InsurerCompany[]>([]);
  const [insurers, setInsurers] = useState<AdminInsurer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [companyFilter, setCompanyFilter] = useState("");
  const [nameSort, setNameSort] = useState<"" | "asc" | "desc">("");
  const [insurerFilter, setInsurerFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");
  const [cityFilter, setCityFilter] = useState("");
  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(1);
  const [togglingId, setTogglingId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [companyRows, insurerRows] = await Promise.all([fetchAdminCompanies(), fetchAdminInsurers()]);
      setCompanies(companyRows);
      setInsurers(insurerRows);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Chargement des entreprises impossible.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const city = cityFilter.trim().toLowerCase();
    return companies.filter((company) => {
      const byInsurer =
        insurerFilter === "" ? true : insurerFilter === "none" ? company.insurer_id === null : company.insurer_id === Number(insurerFilter);
      const byStatus = statusFilter ? company.status === statusFilter : true;
      const byCity = city ? (company.city ?? "").toLowerCase().includes(city) : true;
      const byCompany = companyFilter ? company.name === companyFilter : true;
      const bySearch = q
        ? `${companyCode(company.id)} ${company.name} ${company.sector ?? ""} ${company.rccm ?? ""} ${company.contact_name ?? ""} ${company.phone} ${company.email ?? ""}`
            .toLowerCase()
            .includes(q)
        : true;
      return byInsurer && byStatus && byCity && byCompany && bySearch;
    });
  }, [companies, search, insurerFilter, statusFilter, cityFilter, companyFilter]);

  const companyNames = useMemo(
    () => [...companies.map((company) => company.name)].sort((a, b) => a.localeCompare(b, "fr")),
    [companies],
  );

  const ordered = useMemo(() => {
    if (!nameSort) return filtered;
    const direction = nameSort === "asc" ? 1 : -1;
    return [...filtered].sort((a, b) => a.name.localeCompare(b.name, "fr", { sensitivity: "base" }) * direction);
  }, [filtered, nameSort]);

  const totalPages = Math.max(1, Math.ceil(ordered.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const start = (currentPage - 1) * pageSize;
  const pageData = ordered.slice(start, start + pageSize);
  const withoutInsurer = companies.filter((company) => company.insurer_id === null && company.status === "active").length;

  const handleExport = () =>
    exportTable(
      "entreprises.csv",
      ["Code", "Entreprise", "Secteur", "Ville", "Téléphone", "Email", "Assureur", "Agents", "Ayants droit", "Statut"],
      filtered.map((company) => [
        companyCode(company.id),
        company.name,
        company.sector ?? "",
        company.city ?? "",
        company.phone,
        company.email ?? "",
        company.insurer_name ?? "Sans assureur",
        String(company.agents_count ?? 0),
        String(company.dependants_count ?? 0),
        company.status === "active" ? "ACTIF" : "INACTIF",
      ]),
    );

  const handleToggle = async (company: InsurerCompany) => {
    const next = company.status === "active" ? "inactive" : "active";
    const label = next === "inactive" ? "désactiver" : "activer";
    if (!window.confirm(`Voulez-vous vraiment ${label} « ${company.name} » ?`)) return;
    setTogglingId(company.id);
    try {
      await setAdminCompanyStatus(company.id, next);
      await load();
      showToast("Statut mis à jour.");
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Mise à jour impossible.");
    } finally {
      setTogglingId(null);
    }
  };

  const resetFilters = () => {
    setSearch("");
    setCompanyFilter("");
    setNameSort("");
    setInsurerFilter("");
    setStatusFilter("active");
    setCityFilter("");
    setPage(1);
  };

  return (
    <>
      <Breadcrumbs items={[{ label: "GESTION" }, { label: "Entreprises" }]} />

      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-foreground">Liste des entreprises</h1>
          {loading ? <p className="mt-1 text-sm text-muted">Chargement…</p> : <p className="mt-1 text-sm text-muted">{filtered.length} entreprise(s)</p>}
          {!loading && withoutInsurer > 0 ? (
            <p className="mt-1 text-sm text-amber-700">{withoutInsurer} entreprise(s) active(s) sans assureur : rattachez-les depuis leur fiche ou depuis Assureurs.</p>
          ) : null}
          {error ? <p className="mt-1 text-sm text-red-600">{error}</p> : null}
        </div>
        <Link
          to="/dashboard/companies/create"
          className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-accent hover:text-accent-foreground"
        >
          <Plus size={16} />
          Ajouter une entreprise
        </Link>
      </div>

      <div className="theme-card p-5">
        <div className="header-ribbon filter-panel relative isolate rounded-2xl grid grid-cols-1 gap-3 p-4 md:grid-cols-4">
          <input
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Rechercher (nom, secteur, RCCM, contact…)"
            aria-label="Rechercher une entreprise"
            className={inputClass}
          />
          <select value={companyFilter} onChange={(e) => { setCompanyFilter(e.target.value); setPage(1); }} className={selectClass} aria-label="Filtrer par entreprise">
            <option value="">Toutes les entreprises</option>
            {companyNames.map((name) => (
              <option key={name} value={name}>{name}</option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => setNameSort((current) => (current === "asc" ? "desc" : "asc"))}
            className={toolButton}
            aria-pressed={nameSort !== ""}
          >
            Trier par entreprise{nameSort === "asc" ? " A → Z" : nameSort === "desc" ? " Z → A" : ""}
          </button>
          <select value={insurerFilter} onChange={(e) => { setInsurerFilter(e.target.value); setPage(1); }} className={selectClass} aria-label="Filtrer par assureur">
            <option value="">Tous les assureurs</option>
            <option value="none">Sans assureur</option>
            {insurers.map((insurer) => (
              <option key={insurer.id} value={insurer.id}>{insurer.name}</option>
            ))}
          </select>
          <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }} className={selectClass} aria-label="Filtrer par statut">
            <option value="">Tous les statuts</option>
            <option value="active">Actif</option>
            <option value="inactive">Inactif</option>
          </select>
          <input value={cityFilter} onChange={(e) => { setCityFilter(e.target.value); setPage(1); }} placeholder="Ville" aria-label="Filtrer par ville" className={inputClass} />
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <ExportMenu onExport={handleExport} />
          <button type="button" onClick={printPage} className={toolButton}>
            <Printer size={14} />
            Imprimer
          </button>
          <button type="button" onClick={resetFilters} className={toolButton}>
            <RotateCcw size={14} />
            Réinitialiser
          </button>
          <label className="ml-auto flex items-center gap-2 text-sm text-muted">
            Afficher
            <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }} className={selectClass}>
              {[10, 25, 50].map((size) => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
            entrées
          </label>
        </div>

        <div className="mt-5 overflow-x-auto">
          <table className="w-full min-w-[980px]">
            <thead>
              <tr className="border-b border-border bg-card-muted/50">
                {["#", "Code", "Nom", "Nbre agents", "Nbre ayants droit", "Statut", "Actions"].map((title) => (
                  <th key={title} className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-muted">
                    {title}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pageData.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-sm text-muted">
                    {loading ? "Chargement…" : "Aucune entreprise ne correspond aux filtres."}
                  </td>
                </tr>
              ) : (
                pageData.map((company, index) => (
                  <tr key={company.id} className="border-b border-border last:border-0 hover:bg-card-muted/30">
                    <td className="px-4 py-3 text-sm text-muted">{start + index + 1}</td>
                    <td className="px-4 py-3 font-mono text-xs text-foreground">{companyCode(company.id)}</td>
                    <td className="px-4 py-3">
                      <Link to={`/dashboard/companies/${company.id}`} className="text-sm font-semibold text-foreground hover:text-primary">
                        {company.name}
                      </Link>
                      <p className="text-xs text-muted">
                        {[company.sector, company.city].filter(Boolean).join(" · ") || "—"} ·{" "}
                        {company.insurer_name ? company.insurer_name : <span className="text-amber-700">sans assureur</span>}
                      </p>
                    </td>
                    <td className="px-4 py-3 text-sm tabular-nums text-foreground">{company.agents_count ?? 0}</td>
                    <td className="px-4 py-3 text-sm tabular-nums text-foreground">{company.dependants_count ?? 0}</td>
                    <td className="px-4 py-3">
                      <StatusBadge variant={company.status === "active" ? "success" : "neutral"}>
                        {company.status === "active" ? "ACTIF" : "INACTIF"}
                      </StatusBadge>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <Link to={`/dashboard/companies/${company.id}`} className={`${iconLink} border-teal/50 text-teal hover:bg-teal-soft`} title="Voir" aria-label={`Voir ${company.name}`}>
                          <Eye size={13} />
                        </Link>
                        <Link to={`/dashboard/companies/${company.id}/edit`} className={`${iconLink} border-primary/50 text-primary hover:bg-accent/20 hover:border-accent`} title="Modifier" aria-label={`Modifier ${company.name}`}>
                          <Pencil size={13} />
                        </Link>
                        <button
                          type="button"
                          disabled={togglingId === company.id}
                          onClick={() => void handleToggle(company)}
                          className={`${iconLink} border-yellow/50 text-yellow hover:bg-yellow-soft disabled:opacity-50`}
                          title={company.status === "active" ? "Désactiver" : "Activer"}
                          aria-label={`${company.status === "active" ? "Désactiver" : "Activer"} ${company.name}`}
                        >
                          <Power size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between border-t border-border px-5 py-4 text-sm text-muted">
          <span>
            Affichage de {filtered.length === 0 ? 0 : start + 1} à {Math.min(start + pageSize, filtered.length)} sur {filtered.length} entrées
          </span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              disabled={currentPage === 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="rounded-lg border border-border px-3 py-1.5 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Précédente
            </button>
            <button
              type="button"
              disabled={currentPage >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="rounded-lg border border-border px-3 py-1.5 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Suivante
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
